import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import authService, { SocialUserData } from '../services/auth.service';

interface ValidationErrors {
  [key: string]: string[];
}

interface FirmData {
  firm_name: string;
  firm_address: string;
  firm_city: string;
  firm_state: string;
  firm_zip: string;
  firm_phone: string;
  accept_terms: boolean;
}

const SocialRegister: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [socialData, setSocialData] = useState<SocialUserData | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isExistingUser, setIsExistingUser] = useState(false);
  const [formData, setFormData] = useState<FirmData>({
    firm_name: '',
    firm_address: '',
    firm_city: '',
    firm_state: '',
    firm_zip: '',
    firm_phone: '',
    accept_terms: false,
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isRegistered, setIsRegistered] = useState(false);

  useEffect(() => {
    const fetchUserData = async () => {
      // Extract token and existing_user flag from URL parameters
      const urlToken = searchParams.get('token');
      const existing_user = searchParams.get('existing_user') === 'true';

      if (!urlToken) {
        toast.error('Invalid social login data');
        navigate('/login');
        return;
      }

      setToken(urlToken);
      setIsExistingUser(existing_user);

      try {
        // Get user data from encrypted token
        const userData = await authService.getSocialUserFromToken(urlToken);
        setSocialData(userData);
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.message || 'Failed to load user data';
        toast.error(errorMessage);
        navigate('/login');
      }
    };

    fetchUserData();
  }, [searchParams, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) {
      toast.error('Token is missing');
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      await authService.completeSocialRegistration({
        token,
        ...formData,
      });

      setIsRegistered(true);
      toast.success('Registration successful! Please wait for admin approval.');
    } catch (error: any) {
      const errorResponse = error.response?.data;

      if (errorResponse?.errors) {
        setErrors(errorResponse.errors);
        // Show the first error message as a toast
        const firstErrorField = Object.keys(errorResponse.errors)[0];
        const firstError = errorResponse.errors[firstErrorField][0];
        toast.error(firstError);
      } else {
        toast.error(
          errorResponse?.message || 'Failed to complete registration'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    // Clear error for the field when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  if (!socialData) {
    return (
      <div
        className='d-flex justify-content-center align-items-center'
        style={{ height: '100vh' }}
      >
        <div className='spinner-border text-primary' role='status'>
          <span className='visually-hidden'>Loading...</span>
        </div>
      </div>
    );
  }

  if (isRegistered) {
    return (
      <div className='text-center'>
        <div className='mb-4'>
          <i
            className='ri-checkbox-circle-line text-success'
            style={{ fontSize: '48px' }}
          ></i>
        </div>
        <h4 className='fw-semibold mb-2'>Registration Complete</h4>
        <p className='mb-4'>
          Thank you for registering your law firm! Your account is pending
          approval from our administrators. You will receive an email
          notification once your account has been approved.
        </p>
        <p className='mb-4'>
          Please note that this process may take up to 24 hours.
        </p>
        <Link to='/login' className='btn btn-primary'>
          Return to Login
        </Link>
      </div>
    );
  }

  return (
    <>
      <h4 className='fw-semibold mb-2'>Complete Your Registration</h4>
      <p className='mb-4 text-muted op-7 fw-normal'>
        Welcome {socialData.name}! Please provide your law firm details to
        complete your registration.
      </p>

      {isExistingUser && (
        <div className='alert alert-info mb-4'>
          <i className='ri-information-line me-2'></i>
          We found your account, but your registration is incomplete. Please
          fill out the firm details below to continue.
        </div>
      )}

      <div className='d-flex align-items-center mb-4 p-3 bg-light rounded'>
        {socialData.avatar && (
          <img
            src={socialData.avatar}
            alt={socialData.name}
            className='rounded-circle me-3'
            style={{ width: '40px', height: '40px' }}
          />
        )}
        <div>
          <div className='fw-semibold'>{socialData.name}</div>
          <div className='text-muted small'>{socialData.email}</div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <h5 className='mb-3'>Law Firm Information</h5>

        <div className='mb-3'>
          <label htmlFor='firm_name' className='form-label'>
            Firm Name *
          </label>
          <input
            type='text'
            className={`form-control form-control-lg ${errors.firm_name ? 'is-invalid' : ''}`}
            id='firm_name'
            name='firm_name'
            placeholder='e.g., Smith & Associates Law Firm'
            value={formData.firm_name}
            onChange={handleChange}
            required
          />
          {errors.firm_name && (
            <div className='invalid-feedback'>{errors.firm_name[0]}</div>
          )}
        </div>

        <div className='mb-3'>
          <label htmlFor='firm_address' className='form-label'>
            Address
          </label>
          <input
            type='text'
            className={`form-control form-control-lg ${errors.firm_address ? 'is-invalid' : ''}`}
            id='firm_address'
            name='firm_address'
            placeholder='123 Main Street, Suite 100'
            value={formData.firm_address}
            onChange={handleChange}
          />
          {errors.firm_address && (
            <div className='invalid-feedback'>{errors.firm_address[0]}</div>
          )}
        </div>

        <div className='row'>
          <div className='col-md-6 mb-3'>
            <label htmlFor='firm_city' className='form-label'>
              City
            </label>
            <input
              type='text'
              className={`form-control form-control-lg ${errors.firm_city ? 'is-invalid' : ''}`}
              id='firm_city'
              name='firm_city'
              placeholder='New York'
              value={formData.firm_city}
              onChange={handleChange}
            />
            {errors.firm_city && (
              <div className='invalid-feedback'>{errors.firm_city[0]}</div>
            )}
          </div>
          <div className='col-md-3 mb-3'>
            <label htmlFor='firm_state' className='form-label'>
              State
            </label>
            <input
              type='text'
              className={`form-control form-control-lg ${errors.firm_state ? 'is-invalid' : ''}`}
              id='firm_state'
              name='firm_state'
              placeholder='NY'
              value={formData.firm_state}
              onChange={handleChange}
            />
            {errors.firm_state && (
              <div className='invalid-feedback'>{errors.firm_state[0]}</div>
            )}
          </div>
          <div className='col-md-3 mb-3'>
            <label htmlFor='firm_zip' className='form-label'>
              ZIP Code
            </label>
            <input
              type='text'
              className={`form-control form-control-lg ${errors.firm_zip ? 'is-invalid' : ''}`}
              id='firm_zip'
              name='firm_zip'
              placeholder='10001'
              value={formData.firm_zip}
              onChange={handleChange}
            />
            {errors.firm_zip && (
              <div className='invalid-feedback'>{errors.firm_zip[0]}</div>
            )}
          </div>
        </div>

        <div className='mb-3'>
          <label htmlFor='firm_phone' className='form-label'>
            Phone Number
          </label>
          <input
            type='tel'
            className={`form-control form-control-lg ${errors.firm_phone ? 'is-invalid' : ''}`}
            id='firm_phone'
            name='firm_phone'
            placeholder='(555) 123-4567'
            value={formData.firm_phone}
            onChange={handleChange}
          />
          {errors.firm_phone && (
            <div className='invalid-feedback'>{errors.firm_phone[0]}</div>
          )}
        </div>

        <div className='mb-4'>
          <div className='form-check'>
            <input
              type='checkbox'
              className={`form-check-input ${errors.accept_terms ? 'is-invalid' : ''}`}
              id='accept_terms'
              name='accept_terms'
              checked={formData.accept_terms}
              onChange={handleChange}
              required
            />
            <label className='form-check-label' htmlFor='accept_terms'>
              I accept the terms and conditions
            </label>
            {errors.accept_terms && (
              <div className='invalid-feedback'>{errors.accept_terms[0]}</div>
            )}
          </div>
        </div>

        <button
          type='submit'
          className='btn btn-primary btn-lg w-100'
          disabled={loading}
        >
          {loading ? (
            <>
              <span
                className='spinner-border spinner-border-sm me-2'
                role='status'
                aria-hidden='true'
              ></span>
              Completing Registration...
            </>
          ) : (
            'Complete Registration'
          )}
        </button>

        <div className='text-center mt-4'>
          <p className='mb-0'>
            By completing registration, you will be registered as a law firm
            administrator. Your account will be pending approval from our
            administrators.
          </p>
        </div>
      </form>
    </>
  );
};

export default SocialRegister;
